<script setup lang="ts">
import { computed, ComputedRef, ref } from 'vue';

import { Fact, FactSet } from '@/api/productNutritionFacts';
import { TagReference } from '@/api/productTagReferences';
import certifiedGlutenFree from '@/assets/pdp/product-facts/certified/gluten-free.png';
import certifiedKosher from '@/assets/pdp/product-facts/certified/kosher.svg';
import certifiedKosherDairy from '@/assets/pdp/product-facts/certified/kosher-dairy.svg';
import certifiedKosherPareve from '@/assets/pdp/product-facts/certified/kosher-parve.svg';
import certifiedOrganic from '@/assets/pdp/product-facts/certified/organic.png';
import glutenFree from '@/assets/pdp/product-facts/gluten-free.svg';
import keto from '@/assets/pdp/product-facts/keto.svg';
import noSugarAdded from '@/assets/pdp/product-facts/no-sugar-added.svg';
import noSulfurDioxide from '@/assets/pdp/product-facts/no-sulfur-dioxide.svg';
import saltFree from '@/assets/pdp/product-facts/salt-free.svg';
import sugarFree from '@/assets/pdp/product-facts/sugar-free.svg';
import unsalted from '@/assets/pdp/product-facts/unsalted.svg';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';

interface Icon extends Fact {
  id: string;
  name: string;
  imageSrc?: string;
  modalText: string;
  url?: string;
}

const props = defineProps<{
  displaySeeMoreButton?: boolean;
  isMobile?: boolean;
  perServingNutritionFacts?: FactSet;
  tagsReferences?: TagReference[];
}>();

const selected = ref();
const isSeeMoreActive = ref(true);

const allCertifications: Icon[] = [
  {
    id: 'kosher',
    name: 'Kosher',
    imageSrc: certifiedKosher,
    modalText:
      'This product is certified kosher with rabbinical supervision by OK Kosher Certification.',
    url: '/kosher/',
  },
  {
    id: 'kosher pareve',
    name: 'Kosher Pareve',
    imageSrc: certifiedKosherPareve,
    modalText:
      'Pareve is the Yiddish term for foods containing no meat or dairy ingredients. According to the Jewish dietary laws, whereas meat and milk products may not be cooked or eaten together, pareve foods are considered neutral and may be eaten with either meat or dairy dishes.',
  },
  {
    id: 'certified gluten-free',
    name: 'Certified Gluten-Free',
    imageSrc: certifiedGlutenFree,
    modalText:
      'Certified by the Gluten-Free Food Program (GFFP); any foods that carry the label “gluten-free,” “no gluten,” “free of gluten,” or “without gluten” must contain less than 5 parts per million (ppm) of gluten.',
    url: '/gluten-free',
  },
  {
    id: 'organic',
    name: 'Organic',
    imageSrc: certifiedOrganic,
    modalText: `Certified by the California Certified Organic Farmers (CCOF) certifying agency; according to the USDA, a product can be called organic if it's certified to have grown on soil with no prohibited substances applied for three years before harvest.`,
  },
  {
    id: 'kosher dairy',
    name: 'Kosher Dairy',
    imageSrc: certifiedKosherDairy,
    modalText:
      'All foods derived from, or containing, milk are classified as dairy, including milk, butter, yogurt and all cheese – hard, soft and cream. Even a trace amount of dairy can cause a food to be considered dairy.',
  },
];

const allGenerals: Icon[] = [
  {
    id: 'gluten-free',
    name: 'Gluten-Free',
    imageSrc: glutenFree,
    modalText:
      'According to the FDA, any foods that carry the label “gluten-free,” “no gluten,” “free of gluten,” or “without gluten” must contain less than 20 parts per million (ppm) of gluten.',
    url: '/gluten-free',
  },
  {
    id: 'sugar-free',
    name: 'Sugar Free',
    imageSrc: sugarFree,
    modalText:
      'The FDA defines "sugar-free" as a claim that may be used on a food that contains less than 0.5 g of sugars – per reference amount customarily consumed and per labeled serving.',
  },
  {
    id: 'no-sugar-added',
    name: 'No Sugar Added',
    imageSrc: noSugarAdded,
    modalText:
      'According to the FDA, when manufacturers claim a food has “no added sugars,” it cannot be processed with any sugar or sugar-containing ingredients, though it can have sugar alcohol or artificial sweeteners. Products without added sugar can contain naturally occurring sugar.',
  },
  {
    id: 'salt-free',
    name: 'Salt Free',
    imageSrc: saltFree,
    modalText:
      'According to the FDA, "salt-free" refers to a product with less than 5 mg of salt per serving.',
  },
  {
    id: 'unsalted',
    name: 'Unsalted',
    imageSrc: unsalted,
    modalText:
      'According to the FDA, "unsalted" refers to a product with no salt added during processing; it may not be salt-free or sodium-free unless stated.',
  },
  {
    id: 'no-sulfur-dioxide',
    name: 'No Sulfur Dioxide',
    imageSrc: noSulfurDioxide,
    modalText:
      'This product does not contain Sulfur Dioxide. The FDA says this substance is generally safe when used in accordance with good manufacturing practices; except that it is not used in meats; in food recognized as a source of vitamin B1; on fruits or vegetables intended to be served raw to consumers or sold raw to consumers, or to be presented to consumers as fresh.',
  },
  {
    id: 'keto',
    name: 'Keto',
    imageSrc: keto,
    modalText:
      'The Ketogenic Diet, or keto, is a low-carb, moderate protein, high-fat diet. FDA does not regulate the terms “keto” or “ketogenic” on food labels.',
  },
];

const certifications = computed(() => {
  const actualCertifications = allCertifications.filter((certification) =>
    props.tagsReferences?.find(({ name }) => name === certification.id),
  );

  const kosherIndex = actualCertifications.findIndex(({ id }) => id === 'kosher');
  const kosherPareveIndex = actualCertifications.findIndex(({ id }) => id === 'kosher pareve');
  if (kosherIndex > -1 && kosherPareveIndex > -1) {
    actualCertifications.splice(kosherIndex, 1);
  }

  const kosherDairyIndex = actualCertifications.findIndex(({ id }) => id === 'kosher dairy');
  if (kosherIndex > -1 && kosherDairyIndex > -1 && kosherPareveIndex === -1) {
    actualCertifications.splice(kosherIndex, 1);
  }

  return actualCertifications;
});

const generals = computed(() => {
  const actualGenerals = allGenerals.filter((general) =>
    props.tagsReferences?.find(({ name }) => name === general.id),
  );

  const glutenFreeIndex = actualGenerals.findIndex(({ id }) => id === 'gluten-free');
  const certifiedGlutenFreeIndex = certifications.value.findIndex(
    ({ id }) => id === 'certified gluten-free',
  );
  if (glutenFreeIndex > -1 && certifiedGlutenFreeIndex > -1) {
    actualGenerals.splice(glutenFreeIndex, 1);
  }

  const saltFreeIndex = actualGenerals.findIndex(({ id }) => id === 'salt-free');
  const unsaltedIndex = actualGenerals.findIndex(({ id }) => id === 'unsalted');
  if (saltFreeIndex > -1 && unsaltedIndex > -1) {
    actualGenerals.splice(saltFreeIndex, 1);
  }

  return actualGenerals;
});

const nutritionFacts: ComputedRef<Icon[]> = computed(() => {
  const list = [];

  if (!props.perServingNutritionFacts) return [];

  const { protein, dietaryFiber, vitaminA, vitaminC, vitaminD, calcium, iron, potassium } =
    props.perServingNutritionFacts;

  if (protein?.pdv || protein?.quantity)
    list.push({
      id: 'protein',
      name: 'Protein',
      modalText:
        'General recommendations are to consume 15–30 grams of protein at each meal. Studies show higher intakes — those more than 40 grams — in one sitting are no more beneficial than the recommended 15–30 grams at one time.',
      pdv: protein.pdv,
      quantity: protein.quantity,
      unit: protein.unit,
    });

  if (dietaryFiber?.pdv || dietaryFiber?.quantity)
    list.push({
      id: 'dietary-fiber',
      name: 'Dietary Fiber',
      modalText:
        'In order for a product to be labeled “high fiber,” it must contain 5 grams or more of dietary fiber per serving.',
      pdv: dietaryFiber.pdv,
      quantity: dietaryFiber.quantity,
      unit: dietaryFiber.unit,
    });

  if (vitaminA?.pdv || vitaminA?.quantity)
    list.push({
      id: 'vitamin-a',
      name: 'Vitamin A',
      modalText:
        'The recommended dietary allowance (RDA) is 900 micrograms (mcg) for males, 700 mcg for females, and 300–600 mcg for children and adolescents.',
      pdv: vitaminA.pdv,
      quantity: vitaminA.quantity,
      unit: vitaminA.unit,
    });

  if (vitaminC?.pdv || vitaminC?.quantity)
    list.push({
      id: 'vitamin-c',
      name: 'Vitamin C',
      modalText: 'The current daily value (DV) for vitamin C is 90 mg for men and 75mg for women.',
      pdv: vitaminC.pdv,
      quantity: vitaminC.quantity,
      unit: vitaminC.unit,
    });

  if (vitaminD?.pdv || vitaminD?.quantity)
    list.push({
      id: 'vitamin-d',
      name: 'Vitamin D',
      modalText:
        'The daily value (DV) for vitamin D is 800 IU (20 mcg). Levels of 50 nmol/L (20 ng/mL) or above are adequate for most people for bone and overall health.',
      pdv: vitaminD.pdv,
      quantity: vitaminD.quantity,
      unit: vitaminD.unit,
    });

  if (calcium?.pdv || calcium?.quantity)
    list.push({
      id: 'calcium',
      name: 'Calcium',
      modalText:
        'If the "percent Daily Value" (%DV) of calcium is 20 percent or more per serving, the food is high in calcium. If the %DV is between 10 and 19 percent, the food is a good source of calcium.',
      pdv: calcium.pdv,
      quantity: calcium.quantity,
      unit: calcium.unit,
    });

  if (iron?.pdv || iron?.quantity)
    list.push({
      id: 'iron',
      name: 'Iron',
      modalText:
        'General recommendations are to consume the following amounts of iron: 18 milligrams (mg) a day for women and people assigned female at birth (AFAB). 27 mg a day for pregnant people. 9 mg a day for lactating people. 8 mg a day for men and people assigned male at birth.',
      pdv: iron.pdv,
      quantity: iron.quantity,
      unit: iron.unit,
    });

  if (potassium?.pdv || potassium?.quantity)
    list.push({
      id: 'potassium',
      name: 'Potassium',
      modalText:
        'A food that is considered “high-potassium” generally has 200 mg or more potassium per serving.',
      pdv: potassium.pdv,
      quantity: potassium.quantity,
      unit: potassium.unit,
    });

  return list;
});

const icons = computed(() => {
  const iconsList: Icon[] = certifications.value.concat(generals.value);

  if (iconsList.length < 10) return iconsList.concat(nutritionFacts.value).slice(0, 10);

  return iconsList;
});

const isCertificationIcon = (icon: Icon) =>
  allCertifications.findIndex(({ id }) => id === icon.id) > -1;

const nutritionFactsValue = ({ pdv, quantity, unit }: Icon) => {
  if (!quantity || quantity === 0 || !unit) {
    return `${pdv}%`;
  }

  return `${quantity}${unit}`;
};

const handleSelectedIcon = (icon: Icon) => {
  if (selected.value?.id === icon.id) selected.value = undefined;
  else selected.value = icon;
};
</script>

<template>
  <div v-if="icons.length > 0">
    <div class="px-3 py-3 rounded bg-nuts-stone-100 lg:px-3 lg:pt-4 lg:pb-8">
      <div
        class="grid self-center grid-cols-4 text-center justify-items-center sm:grid-cols-6 lg:grid-cols-5 gap-x-2 lg:gap-x-4 gap-y-3 lg:gap-y-6"
      >
        <div
          v-for="(icon, index) in displaySeeMoreButton && isSeeMoreActive
            ? icons.slice(0, isMobile ? 4 : 6)
            : icons"
          :key="index"
        >
          <UnstyledButton
            class="mt-0 fact"
            data-promo="1"
            :data-promo-creative="`Overview: ${icon.name}`"
            data-promo-name="Merchandising"
            @click="handleSelectedIcon(icon)"
          >
            <div
              class="flex items-center justify-center w-10 h-10 mx-auto bg-white border border-solid rounded-full lg:w-14 lg:h-14"
              :class="[
                icon.id === selected?.id
                  ? 'border-[#88A5AC] shadow-md'
                  : 'border-neutral-300 hover:border-[#A3C6CE]',
                {
                  'row-start-3 row-end-4': selected && index > 4,
                },
              ]"
            >
              <img
                v-if="icon.imageSrc"
                :alt="icon.name"
                :class="
                  isCertificationIcon(icon)
                    ? 'w-8 h-8 lg:w-11 lg:h-11'
                    : 'w-10 h-10 lg:w-14 lg:h-14'
                "
                :src="icon.imageSrc"
              />
              <span
                v-else
                class="text-xs font-bold text-nuts-neutral-700 font-proxima-nova"
                :class="{
                  'text-[12px] lg:text-[17px]': nutritionFactsValue(icon).length < 3,
                  'text-[11px] lg:text-[17px]': nutritionFactsValue(icon).length === 3,
                  'text-[11px] lg:text-[15px]': nutritionFactsValue(icon).length === 4,
                  'text-[9px] lg:text-[13px]': nutritionFactsValue(icon).length === 5,
                }"
              >
                {{ nutritionFactsValue(icon) }}
              </span>
            </div>
            <SmallBodyText
              class="mt-1 font-semibold font-proxima-nova"
              :class="icon.id === selected?.id ? 'text-nuts-cyan-800' : 'text-black'"
            >
              {{ icon.name }}
            </SmallBodyText>
          </UnstyledButton>
        </div>
        <div
          v-if="selected"
          aria-live="polite"
          class="w-full col-start-1 col-end-5 row-start-2 row-end-3 p-3 bg-white rounded shadow-md sm:col-end-6 text-start"
          data-test="product-facts-modal"
        >
          <div class="flex justify-between">
            <BaseBodyText class="font-semibold font-sofia-pro">
              {{ selected.name }}
            </BaseBodyText>
            <UnstyledButton class="mt-0" @click="selected = undefined">
              <img class="w-4 h-4" src="@/assets/close.svg" alt="close menu icon" />
            </UnstyledButton>
          </div>
          <SmallBodyText class="mt-2.5 text-neutral-500 lg:font-semibold font-proxima-nova">
            {{ selected.modalText }}
          </SmallBodyText>
          <RouterLink v-if="selected.url" class="mt-1 text-sm" target="_blank" :to="selected.url">
            <span class="font-bold text-black underline">Learn more</span>
          </RouterLink>
        </div>
      </div>
      <UnstyledButton
        v-if="icons.length > (isMobile ? 4 : 6)"
        class="mt-1 text-sm text-black underline lg:mt-0 font-proxima-nova md:text-base lg:hidden"
        data-promo="1"
        :data-promo-creative="`Overview: See ${isSeeMoreActive ? 'More' : 'Less'}`"
        data-promo-name="Merchandising"
        @click="isSeeMoreActive = !isSeeMoreActive"
      >
        See {{ isSeeMoreActive ? 'more' : 'less' }}
      </UnstyledButton>
    </div>
  </div>
</template>

<style scoped>
.fact:hover p {
  @apply text-nuts-emerald-900;
}
.fact:hover div {
  @apply border shadow-md border-[#A3C6CE];
}
</style>
